import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { devApi } from '../../services/api';
import {
  PageTitle,
  PageDescription,
  Card,
  CardTitle,
  StatGrid,
  StatCard,
  StatValue,
  StatLabel,
  Table,
  ErrorMessage,
  LoadingMessage,
  TwoColumnRow,
  ChartsSideBySide,
  ChartContainer,
  CountryList,
  SubscriptionsGrid,
  PlanCard,
  PlanName,
  PlanCount,
  PlanMeta,
} from './styles';

const AUTH_PIE_COLORS = ['#ff9600', '#4a9eff', '#34c777'];
const COUNTRY_PIE_COLORS = ['#ff9600', '#4a9eff', '#34c777', '#a855f7', '#ef4444', '#22d3ee', '#fbbf24', '#ec4899', '#14b8a6', '#6366f1'];

export default function DevOverview() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    devApi
      .getOverview()
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load overview');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <LoadingMessage>Loading overview…</LoadingMessage>;
  }
  if (error) {
    return (
      <>
        <PageTitle>Overview</PageTitle>
        <ErrorMessage>{error}</ErrorMessage>
      </>
    );
  }
  if (!data) return null;

  const { phase1, phase2, recentSignups } = data;

  const authPieData = [
    { name: 'Google', value: phase1.authSplitPercent?.google ?? 0 },
    { name: 'Email', value: phase1.authSplitPercent?.email ?? 0 },
    { name: 'Both', value: phase1.authSplitPercent?.both ?? 0 },
  ].filter((d) => d.value > 0);

  return (
    <>
      <PageTitle>Overview</PageTitle>
      <PageDescription>Metrics and recent signups for the dev dashboard.</PageDescription>

      <CardTitle style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>Phase 1 – Counts & growth</CardTitle>
      <StatGrid>
        <StatCard>
          <StatValue>{phase1.totalUsers}</StatValue>
          <StatLabel>Total users</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase1.totalCoaches}</StatValue>
          <StatLabel>Coaches</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase1.totalAthletes}</StatValue>
          <StatLabel>Athletes</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase1.newSignups7d}</StatValue>
          <StatLabel>New signups (7d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase1.newSignups30d}</StatValue>
          <StatLabel>New signups (30d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase1.activePaidSubscriptions}</StatValue>
          <StatLabel>Active paid subs</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase1.coachesWithZeroAthletes}</StatValue>
          <StatLabel>Coaches with 0 athletes</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase1.avgAthletesPerCoach}</StatValue>
          <StatLabel>Avg athletes/coach</StatLabel>
        </StatCard>
      </StatGrid>

      <ChartsSideBySide>
        <Card>
          <CardTitle>Auth split</CardTitle>
          <ChartContainer style={{ minHeight: 260 }}>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={authPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}%`}
                  outerRadius={80}
                  dataKey="value"
                >
                  {authPieData.map((_, i) => (
                    <Cell key={i} fill={AUTH_PIE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => `${value}%`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>

        {phase1.usersByCountry && phase1.usersByCountry.length > 0 && (
          <Card>
            <CardTitle>Users by country (top 10)</CardTitle>
            <TwoColumnRow>
              <CountryList>
                {phase1.usersByCountry.map((c: any) => (
                  <li key={c._id}>
                    <span>{c._id || '—'}</span>
                    <span>{c.count}</span>
                  </li>
                ))}
              </CountryList>
              <ChartContainer>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={phase1.usersByCountry.map((c: any) => ({ name: c._id || '—', value: c.count }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {phase1.usersByCountry.map((_: any, index: number) => (
                        <Cell key={index} fill={COUNTRY_PIE_COLORS[index % COUNTRY_PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </TwoColumnRow>
          </Card>
        )}
      </ChartsSideBySide>

      <Card>
        <CardTitle>Subscriptions by plan</CardTitle>
        <SubscriptionsGrid>
          {['hs60', 'hs90', 'hs130', 'hs240'].map((plan) => (
            <PlanCard key={plan}>
              <PlanName>{plan.toUpperCase()}</PlanName>
              <PlanCount>{phase1.subscriptionsByPlan?.[plan] ?? 0}</PlanCount>
              <PlanMeta>subscriptions</PlanMeta>
            </PlanCard>
          ))}
        </SubscriptionsGrid>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <span style={{ color: 'var(--text-gray)', fontFamily: 'Poppins', fontSize: '0.9rem' }}>
            <strong style={{ color: 'var(--text-dark)' }}>Monthly:</strong> {phase1.monthlyYearlySplit?.monthly ?? 0}
          </span>
          <span style={{ color: 'var(--text-gray)', fontFamily: 'Poppins', fontSize: '0.9rem' }}>
            <strong style={{ color: 'var(--text-dark)' }}>Yearly:</strong> {phase1.monthlyYearlySplit?.yearly ?? 0}
          </span>
        </div>
      </Card>

      <CardTitle style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>Phase 2 – Revenue & churn</CardTitle>
      <StatGrid>
        <StatCard>
          <StatValue>€{phase2.mrr?.toFixed(2) ?? '0.00'}</StatValue>
          <StatLabel>MRR</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase2.newPaying7d}</StatValue>
          <StatLabel>New paying (7d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase2.newPaying30d}</StatValue>
          <StatLabel>New paying (30d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase2.cancelled30d}</StatValue>
          <StatLabel>Cancelled (30d)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase2.cancelledTotal}</StatValue>
          <StatLabel>Cancelled (total)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{phase2.nextBillingIn7d}</StatValue>
          <StatLabel>Next billing in 7d</StatLabel>
        </StatCard>
      </StatGrid>

      <Card>
        <CardTitle>Recent signups (last 20)</CardTitle>
        <Table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Auth</th>
              <th>Signed up</th>
            </tr>
          </thead>
          <tbody>
            {(recentSignups || []).map((u: any, i: number) => (
              <tr key={i}>
                <td>
                  {u.firstName} {u.lastName}
                </td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{u.authProvider || '—'}</td>
                <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}
